import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getShowOnMap, setShowOnMap } from '../../../../../store/uislice';
import { store } from '../../../../../store/store';
import { getLocation, selectFloorById } from '../../../redux/spaces-slice';
import { resolveDeskBookable } from '../../util/show-path-finding';
// Session-persistent, per-floor "dirty flag" for desk presence. Once a floor has
// ever reported a non-zero desk count, the Desks segment stays visible for that
// floor for the rest of the session — even if the counts later drop back to 0.
// Module scope (not component state) so it survives CountsOverview unmount/remount
// (e.g. building-view round-trips) without re-evaluating from scratch.
const deskSeenByFloor = new Map();
// Colors sampled from Figma node 1167:1314 ("Switcher").
const PILL_SURFACE = '#232a31'; // background/surface
const CHIPS_SURFACE = '#1a1f24';
const ACTIVE_BLUE = '#1d7ff3'; // accent/blue
const ACTIVE_BORDER = '#68adff';
const Widget = styled.div `
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* No gap — the chips tray butts directly under the segments pill (square top,
     rounded bottom) so the two read as one stacked unit. */
  gap: 0;
`;
const Tabs = styled.div `
  display: grid;
  /* One column per visible segment; each column is at least min-content so a
     long label never clips, and shares slack equally (1fr). With a single
     segment (Rooms only) it spans the full width of the pill. */
  grid-template-columns: repeat(
    ${({ $count }) => $count},
    minmax(min-content, 1fr)
  );
  gap: clamp(0.22rem, 0.28vw, 0.24rem);
  width: 100%;
  padding: clamp(0.28rem, 0.32vw, 0.3rem);
  border-radius: clamp(0.72rem, 0.8vw, 0.76rem);
  /* Figma: bg background/surface #232a31, backdrop-blur 3.5px, no drop shadow. */
  background: ${PILL_SURFACE};
  backdrop-filter: blur(3.5px);
  /* Anchor for the single sliding highlight (see TabIndicator). */
  position: relative;
`;
/*
 * A single blue highlight that glides between tabs. Using one shared element
 * (instead of cross-fading each tab's own background) means there's only ever
 * one blue pill on screen, so the previously-active tab never "retains" a
 * lingering blue during the transition. Positioned/sized in JS to exactly
 * overlay the active tab (widths differ per label), it animates transform +
 * width for a smooth ease-in-out glide.
 */
const TabIndicator = styled.div `
  position: absolute;
  left: 0;
  top: 0;
  box-sizing: border-box;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  transform: translate(
    ${({ $left }) => $left}px,
    ${({ $top }) => $top}px
  );
  border-radius: clamp(0.58rem, 0.64vw, 0.62rem);
  /* Figma: accent/blue fill, #68adff 1px border, drop-shadow 0 4 4 rgba(0,0,0,.4). */
  background: ${ACTIVE_BLUE};
  border: 1px solid ${ACTIVE_BORDER};
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.4);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  z-index: 0;
  pointer-events: none;
  transition: ${({ $animate }) => $animate
    ? 'transform 240ms ease-in-out, width 240ms ease-in-out'
    : 'none'};
`;
const Tab = styled.button `
  appearance: none;
  border: none;
  min-width: 0;
  min-height: clamp(2.55rem, 2.8vw, 2.7rem);
  padding: clamp(0.36rem, 0.42vw, 0.38rem) clamp(0.5rem, 0.6vw, 0.56rem);
  border-radius: clamp(0.58rem, 0.64vw, 0.62rem);
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  font-family: 'Sora', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Sit above the sliding highlight so the label stays crisp. */
  position: relative;
  z-index: 1;
`;
const TabInline = styled.div `
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.32rem;
`;
const TabCount = styled.span `
  font-size: clamp(0.96rem, 1.02vw, 1.02rem);
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
`;
const TabLabel = styled.span `
  /* Figma: count and label share the same size (18px @ frame); only the weight
     differs. Match TabCount's size so "10" and "Rooms" read as one line. */
  font-size: clamp(0.96rem, 1.02vw, 1.02rem);
  font-weight: 400;
  line-height: 1.1;
  white-space: nowrap;
`;
const ChipsTrack = styled.div `
  width: 100%;
  position: relative;
  z-index: 1;
`;
const Chips = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  /* Figma: 17px gap between chips. */
  gap: clamp(0.72rem, 0.94vw, 1.06rem);
  /* Fixed, centered tray inset equally on both sides ("cut off" left and right),
     ~20% narrower than the pill (≈10% trimmed off each end). */
  width: 80%;
  /* Butts directly under the pill (Widget gap is 0). Square top corners meet the
     pill's bottom edge; only the bottom corners are rounded (Figma). */
  margin: 0 auto;
  padding: clamp(0.44rem, 0.6vw, 0.62rem) clamp(1rem, 1.25vw, 1.25rem);
  border-radius: 0 0 clamp(0.44rem, 0.5vw, 0.5rem) clamp(0.44rem, 0.5vw, 0.5rem);
  /* Figma: darker tray surface #1a1f24 (two-tone against the lighter pill). */
  background: ${CHIPS_SURFACE};
`;
const Chip = styled.div `
  display: inline-flex;
  align-items: center;
  gap: clamp(0.28rem, 0.34vw, 0.32rem);
  white-space: nowrap;
`;
const dotColors = {
    available: '#34c759',
    booked: '#ff9f0a',
    occupied: '#ff453a',
};
const Dot = styled.span `
  width: clamp(0.5rem, 0.56vw, 0.54rem);
  height: clamp(0.5rem, 0.56vw, 0.54rem);
  border-radius: 50%;
  flex: none;
  background: ${({ $tone }) => dotColors[$tone]};
`;
const ChipText = styled.span `
  display: inline-flex;
  align-items: baseline;
  gap: 0.24rem;
  font-family: 'Sora', sans-serif;
  /* Figma: text/primary white. */
  color: #ffffff;
`;
const ChipCount = styled.span `
  font-size: clamp(0.72rem, 0.78vw, 0.78rem);
  font-weight: 700;
  line-height: 1;
`;
const ChipLabel = styled.span `
  font-size: clamp(0.66rem, 0.72vw, 0.72rem);
  font-weight: 500;
  line-height: 1;
  opacity: 0.85;
`;
const getCountSet = (source) => {
    var _a, _b, _c;
    return ({
        available: (_a = source === null || source === void 0 ? void 0 : source.availableCount) !== null && _a !== void 0 ? _a : 0,
        booked: (_b = source === null || source === void 0 ? void 0 : source.bookedCount) !== null && _b !== void 0 ? _b : 0,
        occupied: (_c = source === null || source === void 0 ? void 0 : source.occupiedCount) !== null && _c !== void 0 ? _c : 0,
    });
};
const getTotal = (counts) => counts.available + counts.booked + counts.occupied;
const CountsOverview = ({ floorId }) => {
    var _a, _b, _c, _d;
    const { t } = useTranslation();
    const activeView = useSelector(getShowOnMap);
    const floorAnalytics = useSelector((state) => selectFloorById(state, floorId));
    const locationData = useSelector(getLocation);
    // Hierarchical LH `deskBookable` flag (building -> floor) for the floor this
    // overview represents. When false, desks are hidden entirely (no segment).
    // Absent/null/true keep today's behavior.
    const currentFloorNumber = Number((_b = (_a = locationData === null || locationData === void 0 ? void 0 : locationData.children) === null || _a === void 0 ? void 0 : _a.find((child) => child.id === floorId)) === null || _b === void 0 ? void 0 : _b.floorNumber);
    const desksBookable = resolveDeskBookable({
        location: locationData,
        floorNumber: currentFloorNumber,
    });
    const roomCounts = getCountSet((_c = floorAnalytics === null || floorAnalytics === void 0 ? void 0 : floorAnalytics.availabilityInfo) === null || _c === void 0 ? void 0 : _c.roomInfo);
    const deskCounts = getCountSet((_d = floorAnalytics === null || floorAnalytics === void 0 ? void 0 : floorAnalytics.availabilityInfo) === null || _d === void 0 ? void 0 : _d.deskInfo);
    const deskTotal = getTotal(deskCounts);
    // Latch the per-floor desk presence once we see any non-zero desk count.
    useEffect(() => {
        if (deskTotal > 0) {
            deskSeenByFloor.set(floorId, true);
        }
    }, [floorId, deskTotal]);
    // Desks segment is shown when this floor currently has desks OR has ever had
    // them this session (dirty flag) — and only if desks are bookable for the floor.
    const desksSeen = deskTotal > 0 || deskSeenByFloor.get(floorId) === true;
    const showDesks = desksSeen && desksBookable;
    // Effective selection: only Rooms / Desks. Anything else (or Desks while the
    // segment is hidden) resolves to Rooms.
    const currentView = activeView === 'Desks' && showDesks ? 'Desks' : 'Rooms';
    // If the Desks segment is hidden while it was the active view, reset the map
    // selector to Rooms so highlighting doesn't stick on an empty/hidden view.
    useEffect(() => {
        if (!showDesks && activeView === 'Desks') {
            store.dispatch(setShowOnMap({ payload: 'Rooms' }));
        }
    }, [showDesks, activeView]);
    const activeCounts = currentView === 'Desks' ? deskCounts : roomCounts;
    const tabs = [
        {
            key: 'Rooms',
            label: t('floor.show-on-map-toggle.rooms'),
            total: getTotal(roomCounts),
        },
        ...(showDesks
            ? [
                {
                    key: 'Desks',
                    label: t('floor.show-on-map-toggle.desks'),
                    total: deskTotal,
                },
            ]
            : []),
    ];
    // --- Sliding highlight geometry --------------------------------------------
    // Measure the active tab and place the single blue highlight over it. We key
    // the measurement on everything that can change a tab's size/position (view,
    // segment count, and the numbers/labels rendered inside).
    const tabsRef = useRef(null);
    const tabRefs = useRef({
        Rooms: null,
        Desks: null,
    });
    const [indicator, setIndicator] = useState({
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        ready: false,
    });
    // Skip the transition on the very first placement so the highlight doesn't
    // animate in from the top-left corner on mount.
    const [animate, setAnimate] = useState(false);
    useLayoutEffect(() => {
        const measure = () => {
            const activeEl = tabRefs.current[currentView];
            if (!activeEl)
                return;
            setIndicator({
                left: activeEl.offsetLeft,
                top: activeEl.offsetTop,
                width: activeEl.offsetWidth,
                height: activeEl.offsetHeight,
                ready: true,
            });
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [
        currentView,
        showDesks,
        tabs.length,
        activeCounts.available,
        activeCounts.booked,
        activeCounts.occupied,
    ]);
    useEffect(() => {
        // Enable animated glide only after the first (instant) placement.
        if (indicator.ready)
            setAnimate(true);
    }, [indicator.ready]);
    const statuses = [
        {
            key: 'available',
            label: t('floor.counts-widget.available'),
            count: activeCounts.available,
        },
        {
            key: 'booked',
            label: t('floor.counts-widget.booked'),
            count: activeCounts.booked,
        },
        {
            key: 'occupied',
            label: t('floor.counts-widget.occupied'),
            count: activeCounts.occupied,
        },
    ];
    return (_jsxs(Widget, { children: [_jsxs(Tabs, Object.assign({ "$count": tabs.length, ref: tabsRef }, { children: [_jsx(TabIndicator, { "$left": indicator.left, "$top": indicator.top, "$width": indicator.width, "$height": indicator.height, "$visible": indicator.ready, "$animate": animate }), tabs.map((tab) => (_jsx(Tab, Object.assign({ type: "button", "aria-pressed": currentView === tab.key, ref: (el) => {
                            tabRefs.current[tab.key] = el;
                        }, onClick: () => {
                            store.dispatch(setShowOnMap({ payload: tab.key }));
                        } }, { children: _jsxs(TabInline, { children: [_jsx(TabCount, { children: tab.total }), _jsx(TabLabel, { children: tab.label })] }) }), tab.key)))] })), _jsx(ChipsTrack, { children: _jsx(Chips, { children: statuses.map((status) => (_jsxs(Chip, { children: [_jsx(Dot, { "$tone": status.key }), _jsxs(ChipText, { children: [_jsx(ChipCount, { children: status.count }), _jsx(ChipLabel, { children: status.label })] })] }, status.key))) }) })] }));
};
export default CountsOverview;
